/**
 * Bootstraps the PCM recorder audio worklet, captures mic audio at 16kHz
 * mono, and forwards buffers through the provided handler.
 */
export async function startAudioRecorderWorklet(onPcmChunk) {
  if (typeof window === 'undefined') {
    throw new Error('Audio recorder can only run in the browser');
  }

  const audioRecorderContext = new AudioContext({
    sampleRate: 16000,
  });

  // Use inline worklet code with Blob URL (works reliably with all bundlers)
  // This approach is more compatible than using import.meta.url which may not resolve
  // correctly in bundled builds (especially with Rollup)
  const workletCode = `
    class PCMRecorderProcessor extends AudioWorkletProcessor {
      process(inputs) {
        if (!inputs.length || !inputs[0].length) {
          return true;
        }
        const inputChannel = inputs[0][0];
        this.port.postMessage(new Float32Array(inputChannel));
        return true;
      }
    }
    registerProcessor('pcm-recorder-processor', PCMRecorderProcessor);
  `;

  const blob = new Blob([workletCode], { type: 'application/javascript' });
  const workletUrl = URL.createObjectURL(blob);

  await audioRecorderContext.audioWorklet.addModule(workletUrl);

  // Clean up the Blob URL after the worklet is loaded
  URL.revokeObjectURL(workletUrl);

  const micStream = await navigator.mediaDevices.getUserMedia({
    audio: { channelCount: 1 },
  });

  const source = audioRecorderContext.createMediaStreamSource(micStream);
  const audioRecorderNode = new AudioWorkletNode(
    audioRecorderContext,
    'pcm-recorder-processor'
  );

  audioRecorderNode.port.onmessage = (event) => {
    const pcmData = convertFloat32ToPCM(event.data);
    onPcmChunk(pcmData);
  };

  source.connect(audioRecorderNode);
  return [audioRecorderNode, audioRecorderContext, micStream];
}

/**
 * Stops all microphone tracks.
 */
export function stopMicrophone(stream) {
  if (!stream) {
    return;
  }
  stream.getTracks().forEach((track) => track.stop());
}

const INT16_MAX = 0x7fff;

function convertFloat32ToPCM(float32Array) {
  const pcm16Array = new Int16Array(float32Array.length);
  for (let i = 0; i < float32Array.length; i += 1) {
    pcm16Array[i] = Math.max(-1, Math.min(1, float32Array[i])) * INT16_MAX;
  }
  return pcm16Array.buffer;
}


